import * as React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, ColumnSplit, Text } from '@factorialco/deprecated-design-system'

import type { PanesHeaderInlineInfoProps } from 'modules/core/components/PanesHeader'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/reviews/components/Response/BlankSlate'
import ResponseQuestions from 'modules/reviews/components/WriteResponse/ResponseQuestions'
import { useScore } from 'modules/reviews/hooks/useScore'
import { ScoreContext } from 'modules/reviews/lib/PerformanceScoreContext'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'
import type QuestionGroupActor from 'modules/reviews/store/models/QuestionGroupActor'
import type Review from 'modules/reviews/store/models/v2/Review'

import AgreementOnEvaluationSection from '../Agreements'
import ReviewUserTargetBlock from '../ReviewUserTargetBlock'

interface QuestionsProps {
  questionGroup: QuestionGroup
  onSubmit: () => void
  onDraftSaved: () => Promise<void>
  canReview?: boolean
  isReviewActive?: boolean
  assessCompetencies: boolean
}

function Questions({
  questionGroup,
  onSubmit,
  onDraftSaved,
  canReview = true,
  isReviewActive,
  assessCompetencies,
}: QuestionsProps) {
  if (!questionGroup.attributes.questions.length) {
    return <BlankSlate />
  }

  return (
    <ResponseQuestions
      isReviewActive={isReviewActive}
      canReview={canReview}
      onSubmit={onSubmit}
      onDraftSaved={onDraftSaved}
      questionGroup={questionGroup}
      assessCompetencies={assessCompetencies}
    />
  )
}

interface Props {
  review: Review
  questionGroup: QuestionGroup
  inlineInfo: PanesHeaderInlineInfoProps[]
  onDraftSaved: () => Promise<void>
  canReview: boolean
  respondent: QuestionGroupActor | null | undefined
}

export default function Answer({
  review,
  questionGroup,
  inlineInfo,
  onDraftSaved,
  canReview,
  respondent,
}: Props) {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { id } = useParams()
  const respondentJobTitle = respondent?.jobTitle
  const targetUser = questionGroup.target
  const targetUserjobTitle = targetUser && targetUser.jobTitle
  const isSelfReview = questionGroup?.respondentGroup?.attributes.name === 'self'
  const isManagerReview = questionGroup?.respondentGroup?.attributes.name === 'manager'
  const { score } = React.useContext(ScoreContext)

  const onSubmit = () => {
    if (pathname.includes('employees')) {
      return navigate(
        navigation.employees.id.reviews.reviewId.answers.fullPath({
          id: String(id),
          review_id: review.attributes.id,
        })
      )
    } else {
      return navigate(
        navigation.performance.reviews.id.writeReviews.fullPath({ id: review.attributes.id })
      )
    }
  }
  const { modifyScoreInlineInfo } = useScore()
  modifyScoreInlineInfo(inlineInfo, score)

  const isCurrentCycle = questionGroup.attributes.cycle_id === review.currentCycleId

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const currentUserIsReviewRespondent = accessId === respondent?.id
  const showAgreementsSection =
    hasFeature('performance_review_agreements') &&
    questionGroup.isAnswered &&
    isManagerReview &&
    currentUserIsReviewRespondent &&
    (review.isFinished || !isCurrentCycle)

  return (
    <Box flexDirection="column" width="full">
      <Box paddingX="s48">
        {showAgreementsSection && <AgreementOnEvaluationSection questionGroup={questionGroup} />}
      </Box>
      <ColumnSplit colored={false} minWidth="none">
        <PanesHeader
          illustration="surveys"
          title={review.attributes.name}
          description={
            <Text.Normal htmlTag="p" whiteSpace="breakSpaces">
              {review.attributes.description || i18n.t('surveys.answer.description')}
            </Text.Normal>
          }
          inlineInfo={inlineInfo}
        />
        <Box>
          <ReviewUserTargetBlock
            respondent={respondent ? respondent : null}
            description={respondentJobTitle}
            target={isSelfReview ? null : targetUser}
            targetDescription={targetUserjobTitle}
          >
            <Questions
              isReviewActive={review.isActive}
              canReview={canReview}
              onSubmit={onSubmit}
              onDraftSaved={onDraftSaved}
              questionGroup={questionGroup}
              assessCompetencies={review.assessCompetencies}
            />
          </ReviewUserTargetBlock>
        </Box>
      </ColumnSplit>
    </Box>
  )
}
